import { _clientNow, _unixTimestamp } from '@/functions/timezone';
import config from '@/modules/config/functions/config';
import logger from '@/modules/logger/constants/logger';
import isEmptyString from '@thisisdmg/tirecloud-patterns/v2/functions/isEmptyString';
import { isNil, merge } from 'lodash-es';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

interface TokenStoreState {
  _accessToken?: string;
  _accessTokenExpiresAt?: number;
  _refreshToken?: string;
  _refreshTokenExpiresAt?: number;
}

type LoadTokenResult =
  | {
      token: string;
      expiresAt: number;
    }
  | 'expired'
  | 'invalid'
  | undefined;

interface TokenStoreActions {
  /**
   * Set the access token and its expiration date
   * @param value - The access token
   * @returns
   */
  setAccessToken: (value: string, expiresAt: number) => void;

  /**
   * Set the refresh token and its expiration date
   * @param value - The refresh token
   */
  setRefreshToken: (value: string, expiresAt: number) => void;

  /**
   * Clear the access token and refresh token
   */
  clearTokens: () => void;

  /**
   * Get the access token if it's still valid
   * @returns The access token if it's still valid
   */
  getAccessToken: () => LoadTokenResult;

  /**
   * Get the refresh token if it's still valid
   * @returns The refresh token if it's still valid
   */
  getRefreshToken: () => LoadTokenResult;
}

const createStore: Parameters<
  typeof immer<TokenStoreState & TokenStoreActions>
>[0] = (set, get) => ({
  _accessToken: undefined,
  _refreshToken: undefined,
  setAccessToken(value, expiresAt) {
    set((state) => {
      state._accessToken = value;
      state._accessTokenExpiresAt = expiresAt;
    });
  },
  setRefreshToken(value, expiresAt) {
    set((state) => {
      state._refreshToken = value;
      state._refreshTokenExpiresAt = expiresAt;
    });
  },
  clearTokens() {
    set((state) => {
      state._accessToken = undefined;
      state._accessTokenExpiresAt = undefined;
      state._refreshToken = undefined;
      state._refreshTokenExpiresAt = undefined;
    });
  },
  getAccessToken() {
    const state = get();
    if (isEmptyString(state._accessToken) || isNil(state._accessTokenExpiresAt))
      return undefined;

    let expiresAt: number | undefined;
    try {
      expiresAt = state._accessTokenExpiresAt;
    } catch (error) {
      logger.error('auth.token', error, { message: 'Invalid access token' });
      return 'invalid';
    }

    if (expiresAt === undefined) {
      logger.error('auth.token', new Error('Invalid access token'), {
        message: 'Access token does not have an expiration date',
      });
      return 'invalid';
    }
    if (_unixTimestamp(_clientNow()) > state._accessTokenExpiresAt)
      return 'expired';

    return { token: state._accessToken, expiresAt };
  },
  getRefreshToken() {
    if (config('features.auth.enableTokenRefreshing') === false)
      return undefined;

    const state = get();
    if (
      isEmptyString(state._refreshToken) ||
      isNil(state._refreshTokenExpiresAt)
    )
      return undefined;

    let expiresAt: number | undefined;
    try {
      expiresAt = state._refreshTokenExpiresAt;
    } catch (error) {
      logger.error('auth.token', error, { message: 'Invalid refresh token' });
      return 'invalid';
    }

    if (expiresAt === undefined) {
      logger.error('auth.token', new Error('Invalid refresh token'), {
        message: 'Refresh token does not have an expiration date',
      });
      return 'invalid';
    }
    if (_unixTimestamp(_clientNow()) > state._refreshTokenExpiresAt)
      return 'expired';

    return { token: state._refreshToken, expiresAt };
  },
});

export default create(
  devtools(
    persist(immer(createStore), {
      name: 'auth_token',
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState, currentState) =>
        merge(currentState, persistedState),
    })
  )
);
